import {observer} from 'mobx-react-lite'
import React, {useCallback, useMemo, useState} from 'react'
import tw from 'tailwind-styled-components'
import {Lang} from 'yougile-lang'
import {Hub} from 'yougile-model'
import {LinkButton} from '@components/common/base/buttons'
import {Tab} from '@components/common/base/tabs'
import {Dropdown} from '@components/common/dropdown/dropdown'
import {DnDDataObject, useDnDStore} from '@components/draggable'
import {cloneOriginElement} from '@components/draggable/utils'
import {useDroppableRef} from '@components/droppable'
import {BoardMenu} from '@components/upper-panel/board-menu'
import {useBoardTabsStore} from '@components/upper-panel/board-tabs-store'
import {EditableTitle} from '@components/upper-panel/editable-title'
import {noPropagate} from '@containers/hooks'
import {cn} from '@containers/html-utils'
import {SeenBy} from '@extensions/seen-by'

const TwBoardTab = tw(Tab)`
  flex-initial
  min-w-56
  max-w-256
  !h-32
  !py-8
  !pl-12
  !pr-8
  !rounded-b-none
`

const TwEditableTitle = tw(EditableTitle)`
  text-sm-regular
  text-panel-text-primary
  placeholder:text-panel-text-placeholder
  leading-4
  h-16
  min-h-16
  max-h-16
  min-w-24
  max-w-full
`

interface BoardTabProps {
  board: Hub
}

const accepts = ['board'] as ['board']

export const BoardTab = observer(({board}: BoardTabProps) => {
  const [state, store] = useBoardTabsStore()
  const [, dndStore] = useDnDStore()

  const setCurrentBoard = useCallback(
    () => store.setCurrentBoard(board.id, board.companyId),
    [store, board]
  )
  const renameBoard = useCallback(
    () => store.startBoardRenaming(board.id),
    [store, board]
  )
  const active = state.currentBoard?.id === board.id
  const renaming = state.editedBoardId === board.id
  const unread = state.newTasks.get(board.id) ?? 0

  const onDrop = useCallback(
    (obj: DnDDataObject, after?: boolean) =>
      store.dropBoard(obj.payload as Hub, board, after),
    [store, board]
  )
  const canMoveBoard = useMemo(() => store.canMoveBoard(board), [store, board])
  const {setElement: ref} = useDroppableRef({
    accepts,
    onDrop,
    container: '.board-tabs-container',
    showPlaceholder: true,
    disabled: !canMoveBoard
  })
  const startDrag = useCallback(
    (_: string, e: React.MouseEvent) => {
      // prevent pointer changing to text one in Safari
      e.preventDefault()

      // force hover on an inactive dragged tab b/c that tab is already hovered
      // but that hover is lost on drag start
      const placeholder = cloneOriginElement(e.currentTarget as HTMLElement)
      placeholder.style.position = 'static'
      !active && placeholder.classList.add('tab--force-hover')

      // custom invisible ghost
      const ghost = cloneOriginElement(e.currentTarget as HTMLElement)
      ghost.classList.add('invisible', 'pointer-events-none')

      dndStore.onMouseDown(e, {
        data: {type: 'board', payload: board},
        trigger: 'middleX',
        scrollContainers: [
          {selector: '.board-tabs-scroll-container', direction: 'x'}
        ],
        placeholder,
        ghost,
        allowPlaceholderOutsideMovement: true,
        canBeHiddenOnDrag: true
      })
    },
    [dndStore, board, active]
  )

  const [badgeEl, setBadgeEl] = useState<HTMLElement | null>()
  const [badgeHintVisible, setBadgeHintVisible] = useState(false)
  const onBadgeClick = useCallback((e: React.MouseEvent) => {
    e.stopPropagation()
    setBadgeHintVisible(true)
  }, [])
  const onBadgeClickAway = useCallback(
    () => setBadgeHintVisible(false),
    [setBadgeHintVisible]
  )
  const markSeen = useCallback(() => {
    SeenBy.markBoardSeen(board)
    store.syncOnUpdate()
    setBadgeHintVisible(false)
  }, [board, store, setBadgeHintVisible])
  const [forceHover, setForceHover] = useState(false)

  return (
    <TwBoardTab
      ref={ref}
      panelTab
      forcedState={forceHover ? 'hover' : undefined}
      id={board.id}
      title={
        renaming ? (
          <TwEditableTitle
            title={board.title}
            onSubmit={store.renameEditedBoard}
          />
        ) : (
          board.title
        )
      }
      allowLongTitles={renaming}
      onClick={setCurrentBoard}
      onDoubleClick={renameBoard}
      onMouseDown={startDrag}
      active={active}
      badgeUnread
      badgeContent={
        unread ? (
          <>
            {unread > 99 ? '99+' : unread}
            <Dropdown
              content={
                <div className="p-6 bg-grey-l100" onClick={noPropagate}>
                  <span className="text-sm-regular text-primary">
                    {Lang.msg.newTasks1}
                    <b>{unread}</b>
                    {Lang.msg.newTasks2}
                  </span>
                  <LinkButton
                    noPaddings
                    size="small"
                    className="mt-2"
                    onClick={markSeen}
                  >
                    {Lang.msg.markAsSeen}
                  </LinkButton>
                </div>
              }
              reference={badgeEl}
              visible={badgeHintVisible}
              onVisibleChange={onBadgeClickAway}
            />
          </>
        ) : undefined
      }
      onBadgeClick={onBadgeClick}
      badgeRef={setBadgeEl}
      tight
      fadeable={!renaming}
      after={
        <BoardMenu
          board={board}
          onToggle={setForceHover}
          className={cn(
            !active && !forceHover && 'invisible',
            'group-hover/tab:visible',
            'tab__visible'
          )}
        />
      }
      dragClickOnHover
    />
  )
})

export const NewBoardTab = observer(() => {
  const [, store] = useBoardTabsStore()

  const title = (
    <TwEditableTitle
      title=""
      onSubmit={store.addNewBoard}
      placeholder={Lang.context.board.titlePlaceholder}
    />
  )
  return <TwBoardTab id="new-tab" title={title} className="!max-w-256" />
})
